import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import InputField from 'components/fields/InputField';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdContentCopy, MdFileDownload, MdOpenInNew, MdChatBubble, MdSmartToy } from 'react-icons/md';
import GoBack from 'components/actions/GoBack';
import { usePixelMode, resolveEffectivePixelMode } from 'services/pixelMode';
import { fetchOverview, hasLinkedSheet, iframeSnippetFor, integrationTitle, isInstallationDone, isSiteInstalled, PRICES_PATH, TemplateOverview } from 'services/integrations';
import InstallationTab from './installation';
import PagesTab from './pages';
import ActivityPricesPanel from './pricesPanel';
import { useIndustries } from 'services/industryService';
import { PER_TEMPLATE_WEBHOOK_MD_DOC_URL as MD_DOC_URL, buildPerTemplateWebhookAgentInstructions as buildSellerAgentInstructions } from 'services/sellerAgentPrompt';
import { fieldExampleValue } from 'services/formFieldExamples';
import { StyleFields, FormBuilderField, MiniFormSwatch, StylePreview, sampleFieldIds } from './designStudio';
import ThemeApplyPanel from './themeApplyPanel';
import { THEMES_PATH } from './themes';
import { FormTheme as SavedTheme, themeStyleOf, fetchThemes, createTheme, setTemplateTheme } from 'services/formThemes';
import ReactApexChart from 'react-apexcharts';

interface FormTemplate extends StyleFields {
    _id: string;
    name: string;
    templateSlug?: string;
    isUniversal?: boolean;
    industry?: string;
    subIndustry?: string;
    fieldOrder?: string[];
    regions?: string[];
    minimumPrice?: number;
    landingPageUrls?: string[];
    chatbotEnabled?: boolean;
    chatbotButtonText?: string;
    pageChatbotOverrides?: Record<string, boolean>;
    pageStyleOverrides?: Record<string, Partial<StyleFields>>;
    // Shared design (services/formThemes.ts) applied over the style fields above, template-wide and
    // per page. Assigned through PATCH /:id/theme and /:id/page-theme, never through the autosave.
    themeId?: string;
    pageThemeIds?: Record<string, string>;
    pageIndustryOverrides?: Record<string, { industry?: string; subIndustry?: string; validated: boolean }>;
    domainIndustryOverrides?: Record<string, { industry?: string; subIndustry?: string }>;
    confirmedSites?: string[];
    defaultIndustry?: { industry?: string; subIndustry?: string };
    source?: string;
    sheetSyncEnabled?: boolean;
    googleSheetId?: string;
    isDefault: boolean;
}

interface PurchaseOrderVolume {
    subIndustry: string;
    orderCount?: number;
    departments: string[];
    minPricePerLead?: number;
}

interface SiteStat {
    _id: string;
    count: number;
    sold: number;
    revenue: number;
    views: number;
}

// What the integration page autosaves (PUT /form-template/:id): design and Paramètres fields only.
const AUTOSAVED_FIELDS = [
    'name', 'industry', 'subIndustry', 'fieldOrder', 'regions', 'minimumPrice',
    'borderColor', 'borderWidth', 'borderRadius', 'backgroundColor', 'textColor', 'labelColor',
    'buttonBackgroundColor', 'buttonTextColor', 'inputBackgroundColor', 'inputBorderColor', 'inputTextColor',
    'fontFamily', 'fontSize', 'ctaText', 'chatbotButtonText',
] as const;

// Full-page mockup of a seller's site with the chatbot bubble the Pixel script injects (see
// pixelScript.ts) — a complete mock website (nav, hero, content, footer), not just a small
// widget thumbnail, so the "Chatbot" tab shows what a visitor actually sees. The floating button
// bottom-right opens a panel embedding the exact same public lead-form URL a manually-pasted
// universal iframe would use for this page. A separate link opens that same form full-size in a
// real new browser tab, for a true, un-scaled look.
function ChatbotFullPagePreview({ buttonText, buttonBackgroundColor, buttonTextColor, borderRadius, formUrl }: {
    buttonText: string;
    buttonBackgroundColor: string;
    buttonTextColor: string;
    borderRadius: string;
    formUrl: string;
}) {
    const [open, setOpen] = useState(false);

    return (
        <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
            <div className="mb-3 flex items-center justify-between">
                <p className="font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">
                    Aperçu de la page complète
                </p>
                <button
                    type="button"
                    onClick={() => window.open(formUrl, '_blank', 'noopener,noreferrer')}
                    className="flex items-center gap-1.5 rounded-lg border border-leadrs-stroke px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text transition-colors hover:border-leadrs-brand hover:text-leadrs-brand"
                >
                    <MdOpenInNew size={14} />
                    Ouvrir le formulaire dans un nouvel onglet
                </button>
            </div>

            <div className="relative h-[720px] overflow-hidden rounded-xl border border-leadrs-stroke bg-white shadow-sm">
                {/* Fake browser chrome */}
                <div className="flex items-center gap-2 border-b border-leadrs-stroke bg-gray-50 px-4 py-2.5">
                    <div className="flex gap-1.5">
                        <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                        <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                        <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
                    </div>
                    <div className="mx-2 flex-1 rounded-md border border-leadrs-stroke bg-white px-3 py-1">
                        <p className="font-geist text-[10px] text-leadrs-text-soft">votre-site.com</p>
                    </div>
                </div>

                {/* Fake site content — purely decorative placeholders */}
                <div className="h-full overflow-hidden bg-[#f8fafc]">
                    <div className="flex items-center justify-between border-b border-leadrs-stroke/60 bg-white px-8 py-4">
                        <div className="h-4 w-24 rounded bg-gray-300" />
                        <div className="flex gap-4">
                            <div className="h-3 w-14 rounded bg-gray-200" />
                            <div className="h-3 w-14 rounded bg-gray-200" />
                            <div className="h-3 w-14 rounded bg-gray-200" />
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-3 px-8 py-16 text-center">
                        <div className="h-6 w-2/3 max-w-md rounded bg-gray-300" />
                        <div className="h-3 w-1/2 max-w-sm rounded bg-gray-200" />
                        <div className="mt-2 h-9 w-40 rounded-lg bg-gray-200" />
                    </div>
                    <div className="grid grid-cols-3 gap-4 px-8">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="flex flex-col gap-2 rounded-lg border border-leadrs-stroke/60 bg-white p-4">
                                <div className="h-16 w-full rounded bg-gray-100" />
                                <div className="h-3 w-3/4 rounded bg-gray-200" />
                                <div className="h-2.5 w-full rounded bg-gray-100" />
                            </div>
                        ))}
                    </div>
                </div>

                {!open ? (
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        title={buttonText || 'Une question ? Discutons-en !'}
                        aria-label={buttonText || 'Une question ? Discutons-en !'}
                        className="absolute bottom-6 right-6 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-opacity hover:opacity-90"
                        style={{ backgroundColor: buttonBackgroundColor, color: buttonTextColor }}
                    >
                        <MdChatBubble size={24} />
                    </button>
                ) : (
                    <div
                        className="absolute bottom-6 right-6 flex flex-col overflow-hidden bg-white shadow-2xl"
                        style={{ width: 380, height: 600, maxWidth: 'calc(100% - 48px)', maxHeight: 'calc(100% - 48px)', borderRadius }}
                    >
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/10 font-geist text-base text-leadrs-text transition-colors hover:bg-black/20"
                            aria-label="Fermer"
                        >
                            ×
                        </button>
                        <iframe
                            src={`${formUrl}${formUrl.includes('?') ? '&' : '?'}chatbot=1`}
                            title="Aperçu du chatbot"
                            className="h-full w-full flex-1 border-none"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

const DISPLAY_API_URL = CONFIG.apiUrl.replace(/\/$/, '') === 'https://api-leadrs-co.osc-fr1.scalingo.io'
    ? 'https://api.leadrs.co'
    : CONFIG.apiUrl;

// One page per integration (notes/ux-integrations-vendeur.md §4.6). A universal integration
// ("Mon formulaire Leadrs") has Installation · Apparence · Performances; the older per-integration
// API keeps Paramètres · Apparence · Envoi par API · Performances. The webhook view of a universal
// integration ('integration') is only reached from the "⋯" menu (Outils avancés, canSellWithGSheet accounts).
type Tab = 'installation' | 'pages' | 'prix' | 'apparence' | 'performances' | 'parametres' | 'integration';

const TAB_KEYS: Tab[] = ['installation', 'pages', 'prix', 'apparence', 'performances', 'parametres', 'integration'];
// Old ?tab= values (bookmarks, emails, other screens) still land on the right tab.
const LEGACY_TABS: Record<string, Tab> = { design: 'apparence', chatbot: 'apparence', analytics: 'performances', pricing: 'prix', prices: 'prix' };
const parseTab = (raw: string | null): Tab | null => {
    if (!raw) return null;
    if (LEGACY_TABS[raw]) return LEGACY_TABS[raw];
    return TAB_KEYS.includes(raw as Tab) ? (raw as Tab) : null;
};

// The webhook ("Envoi par API" of an older per-integration API) is for canSellWithGSheet accounts only: other sellers
// don't see the tab at all, rather than a locked one.
// A Google Sheet import has no form to style: its activity and price, then its performances.
const tabsFor = (isUniversal: boolean, canWebhook: boolean, isSheet = false): { key: Tab; label: string }[] =>
    isSheet && !isUniversal
        ? [
              { key: 'parametres', label: 'Activité et prix' },
              { key: 'performances', label: 'Performances' },
          ]
        : isUniversal
        ? [
              // One tab per job: install, what each page sells, what each activity is sold at,
              // how the form looks, what it brings in.
              { key: 'installation', label: 'Installation' },
              { key: 'pages', label: 'Pages' },
              { key: 'prix', label: 'Prix' },
              { key: 'apparence', label: 'Apparence' },
              { key: 'performances', label: 'Performances' },
          ]
        : [
              { key: 'parametres', label: 'Paramètres' },
              { key: 'apparence', label: 'Apparence' },
              ...(canWebhook ? [{ key: 'integration' as Tab, label: 'Envoi par API' }] : []),
              { key: 'performances', label: 'Performances' },
          ];

const EditFormTemplate = () => {
    const navigate = useNavigate();
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const { id } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    // null until we know which tab to open (a universal integration opens on Installation until
    // it's done, then on Performances).
    const [tab, setTab] = useState<Tab | null>(parseTab(searchParams.get('tab')));
    const [template, setTemplate] = useState<FormTemplate | null>(null);
    // undefined = not loaded yet; null = no overview entry (shouldn't happen for an owned template).
    const [overview, setOverview] = useState<TemplateOverview | null | undefined>(undefined);
    const [menuOpen, setMenuOpen] = useState(false);
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    // Set by user edits only (handleChange / handleMultiChange), so the autosave below never fires
    // for a template that just came back from the server.
    const dirty = useRef(false);
    const [isFetching, setIsFetching] = useState(true);
    // The seller's saved themes (all integrations) — picked here, edited on the design editor page.
    const [themes, setThemes] = useState<SavedTheme[]>([]);
    // Theme whose "Appliquer à…" panel is open (Thème page par page).
    const [applyingTheme, setApplyingTheme] = useState<SavedTheme | null>(null);
    const [purchaseOrderVolumes, setPurchaseOrderVolumes] = useState<PurchaseOrderVolume[]>([]);
    // Universal templates have no Paramètres tab, so the name is edited inline on the header title
    // instead — click the title to reveal an input, save on blur/Enter.
    const [isEditingName, setIsEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [formFields, setFormFields] = useState<FormBuilderField[]>([]);
    const [user, setUser] = useState<any>(null);
    const [isExportingCsv, setIsExportingCsv] = useState(false);

    const [analyticsStats, setAnalyticsStats] = useState<SiteStat[]>([]);
    const [viewOnlySites, setViewOnlySites] = useState<{ _id: string; views: number }[]>([]);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [weeklyLabels, setWeeklyLabels] = useState<string[]>([]);
    const [weeklySeries, setWeeklySeries] = useState<{ name: string; data: number[] }[]>([]);
    const [weeklyLoading, setWeeklyLoading] = useState(false);

    useEffect(() => {
        fetchTemplate();
        fetchPurchaseOrderVolumes();
        fetchUser();
        axios.get(`${CONFIG.apiUrl}/form-field`).then((res) => setFormFields(res.data.results || [])).catch(() => {});
        refreshThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const refreshThemes = () => {
        fetchThemes().then(setThemes).catch(() => {});
    };

    const { globalMode: globalPixelMode } = usePixelMode();
    const pixelRequired = resolveEffectivePixelMode(globalPixelMode, user) === 'mandatory';

    const refreshOverview = () => {
        if (!id) return;
        fetchOverview(id)
            .then((list) => setOverview(list[0] || null))
            .catch(() => setOverview(null));
    };
    useEffect(refreshOverview, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pick the opening tab once the template (and, for a universal one, its state) is known, and
    // keep the tab consistent with the kind of integration.
    useEffect(() => {
        if (!template) return;
        // The webhook tab depends on the account (canSellWithGSheet): wait for it before second-guessing ?tab=.
        if (tab === 'integration' && !user) return;
        const allowed = tabsFor(!!template.isUniversal, !!user?.canSellWithGSheet, hasLinkedSheet(template)).map((t) => t.key);
        const sheetImport = !template.isUniversal && hasLinkedSheet(template);
        if (tab && (allowed.includes(tab) || (tab === 'integration' && user?.canSellWithGSheet && !sheetImport))) return;
        if (!template.isUniversal) {
            setTab('parametres');
            return;
        }
        if (overview === undefined) return;
        // Open on what's most urgent: install, then a missing price, then pages to check, else results.
        const installed = !!overview && overview.sites.length > 0 && overview.sites.every((s) => isSiteInstalled(s, pixelRequired));
        const unpriced = !!overview && overview.activities.some((a) => a.minimumPrice == null);
        const pagesToDo = !!overview && overview.sites.some((s) => s.pages > s.verifiedPages);
        setTab(!installed ? 'installation' : unpriced ? 'prix' : pagesToDo ? 'pages' : isInstallationDone(overview!, pixelRequired) ? 'performances' : 'installation');
    }, [template, overview, tab, pixelRequired, user]);

    useEffect(() => {
        if (tab === 'performances' && id) {
            fetchAnalytics();
            fetchWeeklyAnalytics();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, id]);

    const switchTab = (t: Tab, params: Record<string, string> = {}) => {
        setTab(t);
        setSearchParams({ tab: t, ...params }, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const fetchUser = async () => {
        try {
            const config = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const response = await axios.get(`${CONFIG.apiUrl}/user/me`, config);
            setUser(response.data);
        } catch (error) {
            console.error('Error fetching user:', error);
        }
    };

    const fetchPurchaseOrderVolumes = async () => {
        try {
            const config = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const response = await axios.get(`${CONFIG.apiUrl}/purchase-order/volumes`, config);
            setPurchaseOrderVolumes(response.data);
        } catch (error) {
            console.error('Error fetching purchase order volumes:', error);
        }
    };

    const fetchTemplate = async () => {
        if (!id) { toast.error('Intégration introuvable'); navigate('/integrations'); return; }
        setIsFetching(true);
        try {
            const config = { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const response = await axios.get(`${CONFIG.apiUrl}/form-template/${id}`, config);
            setTemplate(response.data);
        } catch (error) {
            console.error('Error fetching template:', error);
            toast.error("Erreur lors du chargement de l'intégration");
            navigate('/integrations');
        } finally {
            setIsFetching(false);
        }
    };

    const fetchAnalytics = async () => {
        setAnalyticsLoading(true);
        try {
            const config = { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const res = await axios.get(`${CONFIG.apiUrl}/form-template/${id}/analytics`, config);
            setAnalyticsStats(res.data.stats || []);
            setViewOnlySites(res.data.viewOnlySites || []);
            if (res.data.template) setTemplate(res.data.template);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const fetchWeeklyAnalytics = async () => {
        setWeeklyLoading(true);
        try {
            const config = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const res = await axios.get(`${CONFIG.apiUrl}/form-template/${id}/analytics/monthly`, config);
            setWeeklyLabels(res.data.labels || []);
            setWeeklySeries(res.data.series || []);
        } catch (error) {
            console.error('Error fetching weekly analytics:', error);
        } finally {
            setWeeklyLoading(false);
        }
    };

    // Autosave: every edit (design, field order, Paramètres) is saved shortly after the last change,
    // no Enregistrer / Annuler bar. A universal integration's empty name falls back to the default;
    // the older per-integration API still needs one, so an empty name just isn't saved.
    useEffect(() => {
        if (!template || !dirty.current) return;
        if (!template.isUniversal && !template.name.trim()) return;
        const timer = window.setTimeout(async () => {
            dirty.current = false;
            setSaveState('saving');
            // Only the fields edited on this page: the template loaded with the page may be stale for
            // anything the server changes on its own (pages found by form views, the Pixel or a sitemap,
            // confirmed sites, the bubble switch), and PUT would otherwise write those stale values back.
            const payload: Record<string, unknown> = {};
            AUTOSAVED_FIELDS.forEach((field) => {
                if ((template as any)[field] !== undefined) payload[field] = (template as any)[field];
            });
            if (!template.name.trim()) payload.name = 'Mon formulaire Leadrs';
            try {
                const config = { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') } };
                await axios.put(`${CONFIG.apiUrl}/form-template/${template._id}`, payload, config);
                setSaveState('saved');
            } catch (error) {
                console.error('Error saving integration:', error);
                setSaveState('error');
                toast.error("Les modifications n'ont pas pu être enregistrées");
            }
        }, 800);
        return () => window.clearTimeout(timer);
    }, [template]);

    const handleChange = (field: keyof FormTemplate, value: any) => {
        if (!template) return;
        dirty.current = true;
        setTemplate({ ...template, [field]: value });
    };

    const handleMultiChange = (updates: Partial<FormTemplate>) => {
        if (!template) return;
        dirty.current = true;
        setTemplate({ ...template, ...updates });
    };

    const activeTheme = template?.themeId ? themes.find((t) => t._id === String(template.themeId)) : undefined;

    const selectTheme = async (themeId: string | null) => {
        if (!template || (template.themeId || null) === themeId) return;
        try {
            const updated = await setTemplateTheme(template._id, themeId);
            // Only the theme: the rest of the loaded template may hold edits the autosave hasn't sent yet.
            setTemplate((t) => (t ? { ...t, themeId: updated.themeId } : t));
            refreshThemes();
        } catch (error) {
            console.error('Error selecting theme:', error);
            toast.error('Erreur lors du changement de thème');
        }
    };

    // After "Appliquer à…": the template's theme and page themes from the server, nothing else — no
    // full reload (it would blank the page), and edits not autosaved yet are kept.
    const reloadThemeAssignments = async () => {
        if (!id) return;
        try {
            const res = await axios.get(`${CONFIG.apiUrl}/form-template/${id}`, { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
            setTemplate((t) => (t ? { ...t, themeId: res.data.themeId, pageThemeIds: res.data.pageThemeIds } : t));
        } catch (error) {
            console.error('Error reloading template themes:', error);
        }
    };

    // "+ Nouveau thème": a theme made from what the form looks like now, put on it, then opened in the editor.
    const createThemeFromDesign = async () => {
        if (!template) return;
        const name = window.prompt('Nom du nouveau thème', template.name)?.trim();
        if (!name) return;
        try {
            const look = activeTheme ? { ...themeStyleOf(template), ...themeStyleOf(activeTheme) } : themeStyleOf(template);
            const theme = await createTheme(name.slice(0, 100), look);
            await setTemplateTheme(template._id, theme._id);
            navigate(`${THEMES_PATH}/${theme._id}?from=${template._id}`);
        } catch (error) {
            console.error('Error creating theme:', error);
            toast.error("Le thème n'a pas pu être créé");
        }
    };

    const handleDelete = async () => {
        if (!template) return;
        if (!window.confirm('Supprimer cette intégration ? Les formulaires déjà collés sur vos sites cesseront de fonctionner.')) return;
        try {
            const config = { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
            await axios.delete(`${CONFIG.apiUrl}/form-template/${template._id}`, config);
            toast.success('Intégration supprimée');
            navigate('/integrations');
        } catch (error) {
            console.error('Error deleting integration:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    // Direct PUT of just this field — the chatbot toggle has no per-tab "Enregistrer" bar to
    // route through (see the tab-list exclusion below), so it saves itself on flip.
    const saveChatbotEnabled = async (enabled: boolean) => {
        if (!template) return;
        try {
            const config = { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const res = await axios.put(`${CONFIG.apiUrl}/form-template/${template._id}`, { chatbotEnabled: enabled }, config);
            setTemplate(res.data);
            toast.success(enabled ? 'Bulle activée' : 'Bulle désactivée');
        } catch (error) {
            console.error('Error updating chatbot status:', error);
            toast.error('Erreur lors de la mise à jour de la bulle');
        }
    };

    // Per-page chatbot override: null clears it (page follows the template-wide chatbotEnabled
    // default again), true/false forces the bubble on/off for that exact page.
    const savePageChatbot = async (url: string, enabled: boolean | null) => {
        if (!template) return;
        try {
            const config = { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const res = await axios.patch(`${CONFIG.apiUrl}/form-template/${template._id}/page-chatbot`, { landingPage: url, enabled }, config);
            setTemplate(res.data);
        } catch (error) {
            console.error('Error setting page chatbot override:', error);
            toast.error('Erreur lors de la mise à jour de la bulle pour cette page');
        }
    };

    const startEditingName = () => {
        if (!template) return;
        setNameDraft(template.name || '');
        setIsEditingName(true);
    };

    // Direct PUT on blur/Enter — there's no Paramètres tab (and its bottom Save bar) for a
    // universal template to route this through, so the title itself is the save control.
    const saveName = async () => {
        if (!template) return;
        setIsEditingName(false);
        const nextName = nameDraft.trim() || 'Mon formulaire Leadrs';
        if (nextName === template.name) return;
        try {
            const config = { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const res = await axios.put(`${CONFIG.apiUrl}/form-template/${template._id}`, { ...template, name: nextName }, config);
            setTemplate(res.data);
            toast.success('Nom mis à jour');
        } catch (error) {
            console.error('Error updating name:', error);
            toast.error('Erreur lors de la mise à jour du nom');
        }
    };

    const moveFieldInOrder = (fieldId: string, direction: 'up' | 'down') => {
        if (!template) return;
        const industry = industriesData.find(i => i.name === template.industry);
        const mandatory = industry?.mandatoryFields || [];
        const order = template.fieldOrder || [];
        const visible = [...order, ...mandatory.filter(f => !order.includes(f))];
        const idx = visible.indexOf(fieldId);
        if (idx === -1) return;
        const target = direction === 'up' ? idx - 1 : idx + 1;
        if (target < 0 || target >= visible.length) return;
        const next = [...visible];
        [next[idx], next[target]] = [next[target], next[idx]];
        handleChange('fieldOrder', next);
    };

    const toggleOptionalField = (fieldId: string) => {
        if (!template) return;
        const order = template.fieldOrder || [];
        handleChange('fieldOrder', order.includes(fieldId) ? order.filter(f => f !== fieldId) : [...order, fieldId]);
    };

    const handleIndustryChange = (value: string) => {
        const updates: Partial<FormTemplate> = { industry: value };
        const selectedIndustry = industriesData.find(i => i.name === value);
        updates.subIndustry = (value && selectedIndustry?.subIndustries.length) ? selectedIndustry.subIndustries[0].name : '';
        handleMultiChange(updates);
    };

    const volumeToSellerPrice = (buyerPrice: number) => {
        if (user?.commissionFixedPrice != null) return buyerPrice - user.commissionFixedPrice;
        return buyerPrice / (1 + (user?.commission ?? 15) / 100);
    };

    const normalizeSubIndustry = (value?: string) =>
        (value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();

    const currentVolume = purchaseOrderVolumes.find(
        (v) => normalizeSubIndustry(v.subIndustry) === normalizeSubIndustry(template?.subIndustry)
    );
    const currentRegions = currentVolume?.departments || [];
    const currentPrice = currentVolume?.minPricePerLead != null ? volumeToSellerPrice(currentVolume.minPricePerLead) : null;
    const currentBuyers = currentVolume?.orderCount ?? 0;

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copié !`);
    };

    // Exports every iframe (form template) of the seller with its webhook integration info,
    // using the same field/example logic as the "Intégration API" tab below.
    const exportIntegrationsCsv = async () => {
        setIsExportingCsv(true);
        try {
            const config = { headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') } };
            const response = await axios.get(`${CONFIG.apiUrl}/form-template`, config);
            const templates: FormTemplate[] = response.data || [];

            const fieldLabel = (fieldId: string) => formFields.find(f => f.id === fieldId)?.name || fieldId;
            const fieldExample = (fieldId: string) => fieldExampleValue(fieldLabel(fieldId), formFields.find(f => f.id === fieldId));

            const headers = ['Nom', 'Industrie', 'Sous-industrie', 'Template ID', 'URL webhook (POST)', 'Champs obligatoires', 'Champs optionnels', 'Exemple body JSON'];
            const rows = templates.map((tpl) => {
                const industry = industriesData.find(i => i.name === tpl.industry);
                const mandatory = industry?.mandatoryFields || [];
                const optionalAll = (industry?.fields || []).filter(f => !mandatory.includes(f));
                const order = tpl.fieldOrder || [];
                const visibleFields = [...order, ...mandatory.filter(f => !order.includes(f))];
                const optional = visibleFields.length > 0
                    ? visibleFields.filter(f => !mandatory.includes(f))
                    : optionalAll;

                const bodyExample: Record<string, string> = {};
                [...mandatory, ...optional].forEach((f) => { bodyExample[fieldLabel(f)] = fieldExample(f); });

                return [
                    tpl.name,
                    tpl.industry || '',
                    tpl.subIndustry || '',
                    tpl._id,
                    `${DISPLAY_API_URL}/lead/public/generic-form/${tpl._id}`,
                    mandatory.map(fieldLabel).join('; '),
                    optional.map(fieldLabel).join('; '),
                    JSON.stringify(bodyExample),
                ];
            });

            const csvContent = [headers, ...rows].map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');
            // BOM so Excel opens the accented labels (Prénom, Téléphone...) correctly
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'integrations_webhook.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`${templates.length} iframe${templates.length > 1 ? 's' : ''} exporté${templates.length > 1 ? 's' : ''} en CSV`);
        } catch (error) {
            console.error('Error exporting integrations CSV:', error);
            toast.error("Erreur lors de l'export CSV");
        } finally {
            setIsExportingCsv(false);
        }
    };

    if (industriesLoading || isFetching) {
        return (
            <div className="flex flex-col gap-6 pb-8">
                <div className="flex"><GoBack /></div>
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <div className="flex items-center justify-center py-12">
                        <p className="font-geist text-sm text-leadrs-text-soft">Chargement...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!template || !tab) return null;

    const _previewIndustry = industriesData.find(i => i.name === template.industry);
    const _previewMandatory: string[] = _previewIndustry?.mandatoryFields || [];
    const _previewOrder: string[] = template.fieldOrder || [];
    const previewFieldIds: string[] = [..._previewOrder, ..._previewMandatory.filter(f => !_previewOrder.includes(f))];

    // Base URL for this universal iframe's public form — used by the chatbot's full-page preview.
    const universalFormUrl = `${window.location.origin}/public/lead-form?templateId=${template._id}`;
    // What the form actually looks like: the active theme over the integration's own style — same
    // order as the public form (PublicLeadForm.tsx).
    const editedStyle: StyleFields = activeTheme ? { ...template, ...themeStyleOf(activeTheme) } : template;
    const otherThemeUsers = (activeTheme?.usedBy || []).filter((u) => u.templateId !== template._id);
    // Pages of this form on a theme of their own (a theme deleted since no longer counts).
    const pagesWithTheme = Object.entries(template.pageThemeIds || {})
        .map(([url, id]) => ({ url, theme: themes.find((t) => t._id === id) }))
        .filter((p): p is { url: string; theme: SavedTheme } => !!p.theme);

    // Detected pages grouped by hostname — used by the chatbot tab's per-page overrides.
    const pagesBySite = new Map<string, string[]>();
    (template.landingPageUrls || []).forEach((url) => {
        let hostname = url;
        try { hostname = new URL(url).hostname; } catch { /* malformed URL — bucket under the raw string */ }
        pagesBySite.set(hostname, [...(pagesBySite.get(hostname) || []), url]);
    });
    const totalPages = (template.landingPageUrls || []).length;

    const orderTabContent = (() => {
        const industry = industriesData.find(i => i.name === template.industry);
        if (!industry) return <p className="font-geist text-sm text-leadrs-text-soft">Sélectionnez une industrie pour voir les champs disponibles.</p>;
        const mandatorySet = new Set(industry.mandatoryFields || []);
        const optionalFields = (industry.fields || []).filter(f => !mandatorySet.has(f));
        const hiddenOptional = optionalFields.filter(f => !previewFieldIds.includes(f));
        const typeLabel: Record<string, string> = { string: 'Texte', select: 'Liste', 'multi-select': 'Multi', number: 'Nombre' };
        return (
            <div className="space-y-3">
                <div className="space-y-1.5">
                    {previewFieldIds.map((fieldId, index) => {
                        const fieldDef = formFields.find(f => f.id === fieldId);
                        const isMandatory = mandatorySet.has(fieldId);
                        return (
                            <div key={fieldId} className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-white px-3 py-2">
                                <div className="flex flex-col gap-0.5 text-leadrs-text-soft">
                                    <button type="button" onClick={() => moveFieldInOrder(fieldId, 'up')} disabled={index === 0} className="rounded p-0.5 transition-colors hover:bg-leadrs-bg disabled:opacity-20"><svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M9 5L5 1 1 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></button>
                                    <button type="button" onClick={() => moveFieldInOrder(fieldId, 'down')} disabled={index === previewFieldIds.length - 1} className="rounded p-0.5 transition-colors hover:bg-leadrs-bg disabled:opacity-20"><svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></button>
                                </div>
                                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-leadrs-bg font-geist text-[10px] font-semibold text-leadrs-text-soft">{index + 1}</span>
                                <span className="flex-1 font-geist text-sm font-medium text-leadrs-text">{fieldDef?.name || fieldId}</span>
                                <span className="rounded-full bg-leadrs-bg px-2 py-0.5 font-geist text-[10px] text-leadrs-text-soft">{typeLabel[fieldDef?.type || 'string'] ?? 'Texte'}</span>
                                {isMandatory
                                    ? <span title="Champ obligatoire" className="font-geist text-[10px] text-leadrs-text-soft">🔒</span>
                                    : <button type="button" onClick={() => toggleOptionalField(fieldId)} className="rounded p-0.5 text-leadrs-text-soft transition-colors hover:text-red-500"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></button>
                                }
                            </div>
                        );
                    })}
                </div>
                {hiddenOptional.length > 0 && (
                    <div className="space-y-1.5">
                        <p className="font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">Champs optionnels</p>
                        {hiddenOptional.map(fieldId => {
                            const fieldDef = formFields.find(f => f.id === fieldId);
                            return (
                                <div key={fieldId} className="flex items-center gap-2 rounded-lg border border-dashed border-leadrs-stroke bg-gray-50 px-3 py-2">
                                    <span className="flex-1 font-geist text-sm text-leadrs-text">{fieldDef?.name || fieldId}</span>
                                    <span className="rounded-full bg-leadrs-bg px-2 py-0.5 font-geist text-[10px] text-leadrs-text-soft">{typeLabel[fieldDef?.type || 'string'] ?? 'Texte'}</span>
                                    <button type="button" onClick={() => toggleOptionalField(fieldId)} className="rounded p-0.5 text-leadrs-text-soft transition-colors hover:text-leadrs-brand"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    })();

    // A template that once had both an iframe and a Google Sheet (older per-integration flow) shows as
    // a Google Sheet import only: no iframe sites, views or webhook. A new site integration is made apart.
    const isSheetImport = !template.isUniversal && hasLinkedSheet(template);
    const analyticsMaxCount = analyticsStats.length > 0 ? Math.max(...analyticsStats.map((s) => s.count)) : 1;
    const analyticsTotalLeads = analyticsStats.reduce((s, r) => s + r.count, 0);
    const analyticsTotalSold = analyticsStats.reduce((s, r) => s + r.sold, 0);
    const analyticsTotalRevenue = analyticsStats.reduce((s, r) => s + r.revenue, 0);
    const analyticsTotalViews = analyticsStats.reduce((s, r) => s + r.views, 0) + viewOnlySites.reduce((s, r) => s + r.views, 0);

    // Merge known landingPageUrls with no leads + view-only sites, keyed by URL
    const noLeadSiteMap = new Map<string, number>();
    (template.landingPageUrls || []).filter((url) => !analyticsStats.find((s) => s._id === url)).forEach((url) => noLeadSiteMap.set(url, 0));
    viewOnlySites.forEach((s) => noLeadSiteMap.set(s._id, s.views));
    const noLeadSites = Array.from(noLeadSiteMap.entries()).map(([url, views]) => ({ url, views }));

    const sitesLabel = (() => {
        const hosts = overview?.sites.map((s) => s.hostname) || [];
        if (isSheetImport) return `Import Google Sheet${template.subIndustry ? ` · ${template.subIndustry}` : template.industry ? ` · ${template.industry}` : ''}`;
        if (!template.isUniversal) return `Envoi par API${template.subIndustry ? ` · ${template.subIndustry}` : template.industry ? ` · ${template.industry}` : ''}`;
        if (hosts.length === 0) return 'Pas encore installé sur un site';
        return `Utilisé sur ${hosts.slice(0, 3).join(', ')}${hosts.length > 3 ? ` et ${hosts.length - 3} autre${hosts.length > 4 ? 's' : ''}` : ''}`;
    })();
    const installationDone = !!overview && isInstallationDone(overview, pixelRequired);
    const menuItems = [
        ...(template.isUniversal
            ? [
                  { label: 'Copier le code du formulaire', onClick: () => copyToClipboard(iframeSnippetFor(template._id), 'Code du formulaire') },
                  { label: 'Renommer', onClick: startEditingName },
              ]
            : []),
        ...(template.isUniversal && user?.canSellWithGSheet
            ? [{ label: 'Outils avancés', sub: 'Envoi par webhook, export CSV', onClick: () => switchTab('integration') }]
            : []),
        ...(hasLinkedSheet(template)
            ? [{ label: 'Ouvrir le Google Sheet ↗', onClick: () => window.open(`https://docs.google.com/spreadsheets/d/${template.googleSheetId}/edit`, '_blank') }]
            : []),
        { label: 'Supprimer', danger: true, onClick: handleDelete },
    ];

    return (
        <div className="mx-auto flex max-w-[1140px] flex-col gap-2 pb-12 font-hanken">
            <ToastContainer position="bottom-right" autoClose={5000} />
            <button onClick={() => navigate('/integrations')} className="self-start font-hanken text-[13.5px] font-semibold text-[#241C5C] hover:underline">
                ← Mes intégrations
            </button>

            {/* Header + tabs */}
            <div className="mt-1 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    {template.isUniversal && isEditingName ? (
                        <input
                            autoFocus
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onBlur={saveName}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                if (e.key === 'Escape') setIsEditingName(false);
                            }}
                            placeholder="Mon formulaire Leadrs"
                            className="w-full max-w-md border-b-2 border-[#241C5C] bg-transparent font-newsreader text-[34px] font-medium text-[#14110E] outline-none"
                        />
                    ) : (
                        <h1 className="font-newsreader text-[34px] font-medium leading-[1.1] tracking-[-0.02em] text-[#14110E]">{integrationTitle(template)}</h1>
                    )}
                    <p className="mt-1.5 font-hanken text-[13.5px] text-[#6B6357]">{sitesLabel}</p>
                </div>
                <div className="flex items-center gap-3">
                    <span className="font-hanken text-[12.5px] text-[#8A8172]" aria-live="polite">
                        {saveState === 'saving' ? 'Enregistrement…' : saveState === 'saved' ? 'Enregistré ✓' : saveState === 'error' ? 'Non enregistré' : ''}
                    </span>
                    <div className="relative">
                        <button
                            onClick={() => setMenuOpen((o) => !o)}
                            aria-expanded={menuOpen}
                            aria-label="Plus d'actions"
                            className="rounded-[11px] border border-[#DDD5C9] bg-white px-4 py-2 font-hanken text-[16px] font-semibold text-[#3B352C] transition-colors hover:border-[#241C5C]"
                        >
                            ⋯
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 top-[calc(100%+6px)] z-20 min-w-[250px] rounded-[13px] border border-[#EDE7DE] bg-white p-1.5 shadow-[0_14px_36px_rgba(20,17,14,0.14)]">
                                {menuItems.map((item: any) => (
                                    <button
                                        key={item.label}
                                        onClick={() => {
                                            setMenuOpen(false);
                                            item.onClick();
                                        }}
                                        className="flex w-full flex-col items-start rounded-[9px] px-3 py-2.5 text-left transition-colors hover:bg-[#FAF8F4]"
                                    >
                                        <span className={`font-hanken text-[14px] font-semibold ${item.danger ? 'text-[#A8332B]' : 'text-[#1A1613]'}`}>{item.label}</span>
                                        {item.sub && <span className="font-hanken text-[12px] text-[#8A8172]">{item.sub}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="mb-4 mt-4 flex gap-1 overflow-x-auto border-b border-[#EDE7DE]" role="tablist">
                {tabsFor(!!template.isUniversal, !!user?.canSellWithGSheet, isSheetImport).map((t) => (
                    <button
                        key={t.key}
                        role="tab"
                        aria-selected={tab === t.key}
                        onClick={() => switchTab(t.key)}
                        className={`relative flex items-center gap-2 whitespace-nowrap px-4 py-2.5 font-hanken text-[14px] font-semibold transition-colors ${
                            tab === t.key ? 'text-[#241C5C]' : 'text-[#8A8172] hover:text-[#3B352C]'
                        }`}
                    >
                        {t.label}
                        {t.key === 'installation' && overview && (
                            <span
                                className={`rounded-full px-2 py-[2px] text-[11.5px] font-semibold ${
                                    installationDone ? 'bg-[#D9F0DE] text-[#146334]' : 'bg-[#FCF2D4] text-[#7A5A05]'
                                }`}
                            >
                                {installationDone ? '✓' : 'À terminer'}
                            </span>
                        )}
                        {tab === t.key && <span className="absolute bottom-[-1px] left-2.5 right-2.5 h-0.5 rounded-full bg-[#241C5C]" />}
                    </button>
                ))}
                {tab === 'integration' && template.isUniversal && (
                    <span className="relative px-4 py-2.5 font-hanken text-[14px] font-semibold text-[#241C5C]">
                        Outils avancés
                        <span className="absolute bottom-[-1px] left-2.5 right-2.5 h-0.5 rounded-full bg-[#241C5C]" />
                    </span>
                )}
            </div>

            {/* ── Tab: Installation (universal only) ── */}
            {tab === 'installation' && template.isUniversal && (
                <InstallationTab
                    template={template}
                    onTemplateChange={(t) => setTemplate(t)}
                    user={user}
                    pixelRequired={pixelRequired}
                    overview={overview || null}
                    refreshOverview={refreshOverview}
                    onOpenPages={(params) => switchTab('pages', params)}
                />
            )}

            {/* ── Tab: Pages (universal only) — what each page sells, and what's left to check ── */}
            {tab === 'pages' && template.isUniversal && (
                <PagesTab
                    templateId={template._id}
                    overview={overview || null}
                    refreshOverview={refreshOverview}
                    onTemplateChange={(t) => setTemplate(t)}
                    onOpenPrices={() => switchTab('prix')}
                />
            )}

            {/* ── Tab: Prix (universal only) — price per activity of this form ── */}
            {tab === 'prix' && template.isUniversal && (
                <div className="mt-6 flex flex-col gap-4">
                    <p className="max-w-[720px] font-hanken text-[14px] leading-relaxed text-[#6B6357]">
                        Le prix minimum par lead de chaque activité vendue par ce formulaire. Celui de{' '}
                        <button onClick={() => navigate(PRICES_PATH)} className="font-semibold text-[#241C5C] hover:underline">
                            Mes prix
                        </button>{' '}
                        est proposé par défaut ; un prix fixé ici ne vaut que pour ce formulaire.
                    </p>
                    <ActivityPricesPanel
                        templateId={template._id}
                        overview={overview || null}
                        refreshOverview={refreshOverview}
                        onTemplateChange={(t) => setTemplate(t)}
                        user={user}
                        onShowPages={(a) => switchTab('pages', { activity: `${a.industry}|${a.subIndustry}` })}
                    />
                </div>
            )}

            {/* ── Tab: Paramètres (non-universel uniquement — l'iframe universelle édite son nom
                 directement dans l'en-tête et n'a pas d'industrie/prix fixes) ── */}
            {tab === 'parametres' && !template.isUniversal && (
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="col-span-full">
                            <InputField
                                id="name"
                                label="Nom du template"
                                value={template.name}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)}
                                placeholder="Mon template personnalise"
                            />
                        </div>

                        <div className="col-span-full md:col-span-1">
                            <label className="ml-1 font-geist text-sm font-semibold text-leadrs-text">Industrie</label>
                            <div className="mt-2.5 w-full rounded-xl border border-leadrs-stroke px-2.5 py-3 focus-within:border-leadrs-brand">
                                <select id="industry" className="w-full cursor-pointer bg-white font-geist text-sm text-leadrs-text outline-none" value={template.industry || ''} onChange={(e) => handleIndustryChange(e.target.value)}>
                                    <option value="">Selectionner une industrie</option>
                                    {industriesData.map((ind) => <option key={ind._id} value={ind.name}>{ind.name}</option>)}
                                </select>
                            </div>
                        </div>

                        {template.industry && (
                            <div className="col-span-full md:col-span-1">
                                <label className="ml-1 font-geist text-sm font-semibold text-leadrs-text">Sous-industrie</label>
                                <div className="mt-2.5 w-full rounded-xl border border-leadrs-stroke px-2.5 py-3 focus-within:border-leadrs-brand">
                                    <select id="subIndustry" className="w-full cursor-pointer bg-white font-geist text-sm text-leadrs-text outline-none" value={template.subIndustry || ''} onChange={(e) => handleChange('subIndustry', e.target.value)}>
                                        {industriesData.find(i => i.name === template.industry)?.subIndustries.map((item: any) => (
                                            <option key={item.name} value={item.name}>{item.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div className="col-span-full md:col-span-1">
                            <label htmlFor="minimumPrice" className="ml-1 font-geist text-sm font-semibold text-leadrs-text">
                                Prix minimum par lead de cette intégration
                            </label>
                            <div className="mt-2.5 w-full rounded-xl border border-leadrs-stroke px-2.5 py-3 focus-within:border-leadrs-brand">
                                <input
                                    id="minimumPrice"
                                    type="number"
                                    className="w-full bg-white font-geist text-sm text-leadrs-text outline-none placeholder:text-leadrs-text-soft"
                                    value={template.minimumPrice?.toString() || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const value = e.target.value ? parseFloat(e.target.value) : undefined;
                                        handleChange('minimumPrice', value as any);
                                    }}
                                    placeholder="Ex: 15"
                                />
                            </div>
                            <p className="ml-1 mt-1.5 font-geist text-xs text-leadrs-text-soft">
                                {overview?.accountPrice != null
                                    ? `Prix du compte pour cette activité : ${overview.accountPrice} €. `
                                    : "Pas de prix du compte pour cette activité. "}
                                Le prix ci-dessus est propre à cette intégration.{' '}
                                <button type="button" onClick={() => navigate(PRICES_PATH)} className="font-semibold text-leadrs-brand hover:underline">
                                    Mes prix
                                </button>
                            </p>
                        </div>

                        {!isSheetImport && template.landingPageUrls && template.landingPageUrls.length > 0 && (
                            <div className="col-span-full rounded-lg border border-leadrs-stroke bg-leadrs-bg p-3">
                                <p className="mb-2 font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">
                                    Sites d'intégration
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {template.landingPageUrls.map((url) => (
                                        <span key={url} className="inline-flex items-center gap-1 rounded-full border border-leadrs-stroke bg-white px-2.5 py-1 font-geist text-xs text-leadrs-text">
                                            <svg className="h-3 w-3 shrink-0 text-leadrs-text-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                                            </svg>
                                            {url.replace(/^https?:\/\//, '')}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-2 font-geist text-[10px] text-leadrs-text-soft">
                                    Ces sites sont enregistrés automatiquement lorsque vous copiez l'URL ou l'iframe.
                                </p>
                            </div>
                        )}

                        <div className="col-span-full rounded-lg border border-leadrs-stroke bg-leadrs-bg p-3">
                            <p className="mb-1.5 font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">Ordre d'achat</p>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <p className="font-geist text-[10px] uppercase tracking-wide text-leadrs-text-soft">Acheteurs</p>
                                    <p className="font-geist text-sm font-semibold text-leadrs-text">{currentBuyers}</p>
                                </div>
                                <div>
                                    <p className="font-geist text-[10px] uppercase tracking-wide text-leadrs-text-soft">Prix</p>
                                    <p className="font-geist text-sm font-semibold text-leadrs-green-focus">
                                        {currentPrice != null && currentPrice >= 10 ? `${currentPrice.toFixed(2)}€` : '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="font-geist text-[10px] uppercase tracking-wide text-leadrs-text-soft">Régions</p>
                                    <p className="font-geist text-xs font-medium text-leadrs-text-soft">
                                        {currentRegions.length > 0 ? `${currentRegions.length} région${currentRegions.length > 1 ? 's' : ''}` : '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Tab: Apparence — which design this form uses; editing happens on the design editor page ── */}
            {tab === 'apparence' && (
                <div className="overflow-hidden rounded-xl border border-leadrs-stroke bg-white">
                    <div className="border-b border-leadrs-stroke px-6 py-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h2 className="font-crimson text-xl font-semibold text-leadrs-text">Design du formulaire</h2>
                            <button type="button" onClick={() => navigate(THEMES_PATH)} className="font-geist text-xs font-semibold text-leadrs-text-soft hover:text-leadrs-text">
                                Gérer mes thèmes →
                            </button>
                        </div>
                        <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                            Il s'applique à toutes les pages de ce formulaire, sauf celles qui ont leur propre thème.
                        </p>
                        <div className="mt-4 flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                            {[null, ...themes].map((theme) => {
                                const selected = theme ? activeTheme?._id === theme._id : !activeTheme;
                                const look = theme ? { ...template, ...themeStyleOf(theme) } : template;
                                const uses = theme?.usedBy?.length || 0;
                                return (
                                    <button key={theme?._id || 'own'} type="button" onClick={() => selectTheme(theme?._id || null)}
                                        className={`flex-shrink-0 rounded-xl border-2 p-3 text-left transition-all duration-200 hover:scale-[1.04] hover:shadow-md ${selected ? 'border-leadrs-brand shadow-md ring-2 ring-leadrs-brand/20' : 'border-leadrs-stroke hover:border-leadrs-brand/50'}`}
                                        style={{ minWidth: 112, maxWidth: 140 }}
                                    >
                                        <MiniFormSwatch bg={look.backgroundColor} accent={look.buttonBackgroundColor} text={look.textColor} />
                                        <p className="truncate font-geist text-xs font-semibold leading-tight text-leadrs-text">{theme ? theme.name : 'Design propre'}</p>
                                        <p className="mt-0.5 truncate font-geist text-[10px] leading-tight text-leadrs-text-soft">
                                            {theme ? `${uses} formulaire${uses > 1 ? 's' : ''}` : 'Ce formulaire seul'}
                                        </p>
                                    </button>
                                );
                            })}
                            <button type="button" onClick={createThemeFromDesign}
                                className="flex flex-shrink-0 flex-col items-center justify-center rounded-xl border-2 border-dashed border-leadrs-stroke p-3 text-center transition-colors hover:border-leadrs-brand/50"
                                style={{ minWidth: 112 }}
                            >
                                <span className="font-geist text-xl text-leadrs-text-soft">+</span>
                                <span className="mt-1 font-geist text-xs font-semibold text-leadrs-text">Nouveau thème</span>
                                <span className="mt-0.5 font-geist text-[10px] text-leadrs-text-soft">à partir de ce design</span>
                            </button>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-leadrs-bg p-3">
                            <p className="font-geist text-xs text-leadrs-text">
                                {activeTheme
                                    ? otherThemeUsers.length > 0
                                        ? <>Thème <span className="font-semibold">« {activeTheme.name} »</span>, partagé avec : {otherThemeUsers.map((u) => u.name).join(', ')}.</>
                                        : <>Thème <span className="font-semibold">« {activeTheme.name} »</span>, utilisé uniquement ici.</>
                                    : <>Design propre à ce formulaire.</>}
                            </p>
                            <button type="button"
                                onClick={() => navigate(activeTheme ? `${THEMES_PATH}/${activeTheme._id}?from=${template._id}` : `/integrations/${template._id}/design`)}
                                className="rounded-lg bg-leadrs-brand px-3 py-1.5 font-geist text-xs font-semibold text-white hover:opacity-90"
                            >
                                {activeTheme ? `Modifier « ${activeTheme.name} »` : 'Modifier le design'} →
                            </button>
                        </div>
                    </div>

                    {/* Field order only means something with a fixed industry: a universal form's fields
                        come from each page's activity, so it only gets the preview. */}
                    {template.industry ? (
                        <div className="grid grid-cols-1 divide-y divide-leadrs-stroke xl:grid-cols-2 xl:divide-x xl:divide-y-0">
                            <div className="p-6">
                                <p className="mb-3 font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">Ordre des champs</p>
                                {orderTabContent}
                            </div>
                            <StylePreview values={editedStyle} previewFieldIds={previewFieldIds} formFields={formFields} />
                        </div>
                    ) : (
                        <StylePreview values={editedStyle} previewFieldIds={previewFieldIds.length > 0 ? previewFieldIds : sampleFieldIds(formFields)} formFields={formFields} />
                    )}
                </div>
            )}

            {/* ── Apparence, universal only: pages on a theme of their own, set from the "Appliquer à…" panel. ── */}
            {tab === 'apparence' && template.isUniversal && totalPages > 0 && (
                <div className="mt-6 rounded-xl border border-leadrs-stroke bg-white p-6">
                    <h2 className="font-crimson text-xl font-semibold text-leadrs-text">Thème page par page</h2>
                    <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                        Une page peut avoir son propre thème, par exemple une page promo aux couleurs d'une campagne.
                    </p>
                    {pagesWithTheme.length > 0 ? (
                        <div className="mt-4 space-y-1.5">
                            {pagesWithTheme.map(({ url, theme }) => (
                                <div key={url} className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="truncate font-geist text-xs text-leadrs-text-soft" title={url}>{url.replace(/^https?:\/\//, '')}</span>
                                    <span className="rounded-full bg-leadrs-bg px-2 py-0.5 font-geist text-[11px] font-semibold text-leadrs-text">{theme.name}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="mt-3 font-geist text-xs text-leadrs-text-soft">Toutes les pages suivent le design du formulaire.</p>
                    )}
                    {themes.length > 0 ? (
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <span className="font-geist text-xs text-leadrs-text">Appliquer</span>
                            <select
                                value=""
                                onChange={(e) => setApplyingTheme(themes.find((t) => t._id === e.target.value) || null)}
                                className="rounded-lg border border-leadrs-stroke bg-white px-2 py-1 font-geist text-xs text-leadrs-text outline-none focus:border-leadrs-brand"
                            >
                                <option value="" disabled>un thème…</option>
                                {themes.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                            </select>
                            <span className="font-geist text-xs text-leadrs-text">à des pages</span>
                        </div>
                    ) : (
                        <p className="mt-4 font-geist text-xs text-leadrs-text-soft">Créez un thème pour l'appliquer à certaines pages.</p>
                    )}
                </div>
            )}

            {applyingTheme && (
                <ThemeApplyPanel
                    theme={applyingTheme}
                    focusTemplateId={template._id}
                    onClose={() => setApplyingTheme(null)}
                    onApplied={() => { reloadThemeAssignments(); refreshThemes(); }}
                />
            )}

            {/* ── Apparence, universal only: the bubble. Never an alternative to the form in the page —
                 a complement, offered once the form is live on a site (confirmedSites is filled by the
                 form's own views, see POST /form-template/:id/view). Off by default. ── */}
            {tab === 'apparence' && template.isUniversal && (() => {
                const liveSites = (template.confirmedSites || []).length;
                const bubbleAvailable = liveSites > 0;
                return (
                <div className="mt-6 flex flex-col gap-6">
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h2 className="font-crimson text-xl font-semibold text-leadrs-text">Bulle, en complément du formulaire</h2>
                            <label className={`relative inline-flex items-center ${bubbleAvailable || template.chatbotEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                <input
                                    type="checkbox"
                                    checked={!!template.chatbotEnabled}
                                    disabled={!bubbleAvailable && !template.chatbotEnabled}
                                    onChange={(e) => saveChatbotEnabled(e.target.checked)}
                                    className="peer sr-only"
                                    aria-label="Activer la bulle"
                                />
                                <div className="h-6 w-11 rounded-full bg-leadrs-stroke transition-colors peer-checked:bg-leadrs-brand" />
                                <div className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                            </label>
                        </div>
                        <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                            Un bouton en bas à droite de vos pages, qui ouvre le même formulaire. Il s'ajoute au formulaire
                            dans la page, il ne le remplace pas.
                        </p>
                        <p className="mt-3 font-geist text-xs text-leadrs-text-soft">
                            {!bubbleAvailable ? (
                                <>
                                    Disponible une fois le formulaire en ligne sur votre site.{' '}
                                    <button type="button" onClick={() => switchTab('installation')} className="font-semibold text-leadrs-brand underline">
                                        Terminer l'installation
                                    </button>
                                </>
                            ) : (
                                `La bulle s'affiche sur les sites où le formulaire est en ligne : ${(template.confirmedSites || []).join(', ')}.`
                            )}
                        </p>
                    </div>

                    {/* Per-page override — lets a seller turn the chatbot on/off for one exact page
                        independently of the template-wide default above, e.g. to keep it off a
                        legal/CGU page while it stays on everywhere else. */}
                    {totalPages > 0 && (
                        <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                            <h2 className="font-crimson text-xl font-semibold text-leadrs-text">Bulle page par page</h2>
                            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                Par défaut, chaque page suit le réglage ci-dessus. Forcez-le au cas par cas ici, par
                                exemple pour retirer la bulle d'une seule page tout en la gardant ailleurs sur le même site.
                            </p>
                            <div className="mt-4 divide-y divide-leadrs-stroke">
                                {Array.from(pagesBySite.entries()).map(([hostname, urls]) => (
                                    <div key={hostname} className="py-2 first:pt-0 last:pb-0">
                                        <p className="mb-1.5 font-geist text-xs font-semibold text-leadrs-text-soft">{hostname}</p>
                                        <div className="space-y-1.5">
                                            {urls.map((url) => {
                                                const path = url.replace(/^https?:\/\//, '').replace(hostname, '') || '/';
                                                const override = template.pageChatbotOverrides?.[url];
                                                const value = override === true ? 'on' : override === false ? 'off' : 'auto';
                                                return (
                                                    <div key={url} className="flex flex-wrap items-center justify-between gap-2">
                                                        <span className="truncate font-geist text-xs text-leadrs-text-soft" title={url}>{path}</span>
                                                        <select
                                                            value={value}
                                                            onChange={(e) => {
                                                                const next = e.target.value;
                                                                savePageChatbot(url, next === 'auto' ? null : next === 'on');
                                                            }}
                                                            className="rounded-lg border border-leadrs-stroke bg-white px-2 py-1 font-geist text-[11px] text-leadrs-text outline-none focus:border-leadrs-brand"
                                                        >
                                                            <option value="auto">Suit le réglage global ({template.chatbotEnabled ? 'activé' : 'désactivé'})</option>
                                                            <option value="on">Toujours activé</option>
                                                            <option value="off">Toujours désactivé</option>
                                                        </select>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <ChatbotFullPagePreview
                        buttonText={template.chatbotButtonText || 'Une question ? Discutons-en !'}
                        buttonBackgroundColor={editedStyle.buttonBackgroundColor}
                        buttonTextColor={editedStyle.buttonTextColor}
                        borderRadius={editedStyle.borderRadius}
                        formUrl={universalFormUrl}
                    />
                </div>
                );
            })()}

            {/* ── Tab: Envoi par API (webhook) / Outils avancés — canSellWithGSheet accounts only ── */}
            {tab === 'integration' && user?.canSellWithGSheet && !isSheetImport && (() => {
                const industry = industriesData.find(i => i.name === template.industry);
                const mandatory = industry?.mandatoryFields || [];
                const optionalAll = (industry?.fields || []).filter(f => !mandatory.includes(f));
                const optional = previewFieldIds.length > 0
                    ? previewFieldIds.filter(f => !mandatory.includes(f))
                    : optionalAll;

                const fieldLabel = (fieldId: string) => formFields.find(f => f.id === fieldId)?.name || fieldId;
                const fieldExample = (fieldId: string) => fieldExampleValue(fieldLabel(fieldId), formFields.find(f => f.id === fieldId));

                const bodyExample: Record<string, string> = {};
                [...mandatory, ...optional].forEach((f) => { bodyExample[fieldLabel(f)] = fieldExample(f); });

                const webhookUrl = `${DISPLAY_API_URL}/lead/public/generic-form/${template._id}`;
                const jsonExample = JSON.stringify(bodyExample, null, 2);
                const curlExample = `curl -X POST "${webhookUrl}" \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(bodyExample)}'`;
                const agentInstructions = buildSellerAgentInstructions(webhookUrl, mandatory.map(fieldLabel), optional.map(fieldLabel));

                return (
                    <div className="flex flex-col gap-4">
                        {/* Vibe-coding banner — same pattern as profile/apiDocs/index.tsx for buyers */}
                        <div className="rounded-xl border border-leadrs-brand bg-leadrs-text px-6 py-5">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
                                    <MdSmartToy className="h-5 w-5 text-white" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-geist text-base font-semibold text-white">
                                        Vous vibe-codez ? Branchez votre CRM ou votre backend directement sur ce webhook.
                                    </h3>
                                    <p className="mt-1 font-geist text-sm text-white/70">
                                        Copiez ces instructions et le lien du fichier Markdown ci-dessous, collez-les dans votre
                                        agent IA préféré (Cursor, Claude Code, Copilot...), et laissez-le brancher cette
                                        intégration pour lui envoyer des leads directement.
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <button
                                            onClick={() => copyToClipboard(agentInstructions, "Instructions pour l'agent")}
                                            type="button"
                                            className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 font-geist text-xs font-medium text-leadrs-text hover:opacity-90"
                                        >
                                            <MdContentCopy className="h-4 w-4" />
                                            Copier les instructions pour l'agent
                                        </button>
                                        <button
                                            onClick={() => copyToClipboard(MD_DOC_URL, 'Lien .md')}
                                            type="button"
                                            className="flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-2 font-geist text-xs font-medium text-white hover:bg-white/10"
                                        >
                                            <MdContentCopy className="h-4 w-4" />
                                            Copier le lien .md
                                        </button>
                                        <a
                                            href={MD_DOC_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-2 font-geist text-xs font-medium text-white hover:bg-white/10"
                                        >
                                            Ouvrir le .md
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Intro + endpoint */}
                        <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <h2 className="font-crimson text-2xl font-semibold text-leadrs-text">Intégration via webhook (API)</h2>
                                <button
                                    onClick={exportIntegrationsCsv}
                                    disabled={isExportingCsv}
                                    type="button"
                                    className="flex items-center gap-1.5 rounded-lg border border-leadrs-stroke bg-white px-3 py-2 font-geist text-sm font-medium text-leadrs-text transition-colors hover:border-leadrs-brand disabled:opacity-50"
                                >
                                    <MdFileDownload size={16} />
                                    {isExportingCsv ? 'Export...' : 'Exporter tous les iframes (CSV)'}
                                </button>
                            </div>
                            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                Envoyez vos leads directement depuis votre CRM, votre backend ou un outil d'automatisation (Zapier, Make, n8n...) plutôt que d'utiliser l'iframe. Chaque requête reçue sur cette URL crée un lead pour{' '}
                                <strong>{template.industry || 'cette industrie'}</strong>
                                {template.subIndustry ? <> ({template.subIndustry})</> : null}
                                {' '}et le route automatiquement vers un acheteur disponible.
                            </p>

                            {!template.industry && (
                                <div className="mt-4 rounded-lg border border-leadrs-stroke bg-leadrs-yellow-soft p-3">
                                    <p className="font-geist text-xs text-leadrs-yellow-focus">
                                        Sélectionnez une industrie dans l'onglet Paramètres pour afficher les champs attendus par cette intégration.
                                    </p>
                                </div>
                            )}

                            <div className="mt-5">
                                <label className="mb-2 block font-geist text-sm font-bold text-leadrs-text">Endpoint</label>
                                <div className="flex items-center gap-2">
                                    <span className="rounded-lg bg-leadrs-green-soft px-2.5 py-3 font-mono text-xs font-bold text-leadrs-green-focus">POST</span>
                                    <input type="text" readOnly value={webhookUrl} className="flex-1 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3 font-mono text-xs text-leadrs-text" />
                                    <button
                                        onClick={() => copyToClipboard(webhookUrl, 'URL')}
                                        className="flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-3 py-2.5 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80"
                                        type="button"
                                    >
                                        <MdContentCopy size={16} />
                                        Copier
                                    </button>
                                </div>
                                <p className="mt-2 font-geist text-xs text-leadrs-text-soft">
                                    En-tête requis : <span className="font-mono font-semibold">Content-Type: application/json</span>. Aucune authentification n'est requise : cette URL contient l'identifiant unique de votre formulaire et fait office de secret, ne la partagez pas publiquement.
                                </p>
                            </div>
                        </div>

                        {/* Expected fields */}
                        <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                            <h3 className="font-geist text-base font-bold text-leadrs-text">Champs attendus dans le body JSON</h3>
                            <p className="mt-1 mb-4 font-geist text-xs text-leadrs-text-soft">
                                Les clés doivent correspondre exactement aux libellés ci-dessous (accents compris), déterminés par l'industrie {template.industry ? <>« {template.industry} »</> : 'sélectionnée'}.
                            </p>
                            <div className="flex flex-col gap-1.5">
                                {mandatory.map((f) => (
                                    <div key={f} className="flex items-center justify-between gap-3 rounded-lg border border-leadrs-stroke px-3 py-2">
                                        <span className="font-mono text-sm text-leadrs-text">{fieldLabel(f)}</span>
                                        <span className="rounded-full bg-leadrs-red-soft px-2 py-0.5 font-geist text-[10px] font-semibold text-leadrs-red-focus">Obligatoire</span>
                                    </div>
                                ))}
                                {optional.map((f) => (
                                    <div key={f} className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-leadrs-stroke px-3 py-2">
                                        <span className="font-mono text-sm text-leadrs-text-soft">{fieldLabel(f)}</span>
                                        <span className="rounded-full bg-leadrs-bg px-2 py-0.5 font-geist text-[10px] font-semibold text-leadrs-text-soft">Optionnel</span>
                                    </div>
                                ))}
                                {mandatory.length === 0 && optional.length === 0 && (
                                    <p className="font-geist text-sm text-leadrs-text-soft">Aucune industrie sélectionnée pour ce template.</p>
                                )}
                            </div>
                        </div>

                        {/* JSON body example */}
                        <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                            <h3 className="font-geist text-base font-bold text-leadrs-text">Exemple de body JSON</h3>
                            <div className="relative mt-3">
                                <pre className="max-h-72 overflow-auto rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3 pr-24 font-mono text-xs text-leadrs-text">{jsonExample}</pre>
                                <button
                                    className="absolute right-2 top-2 flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-2.5 py-1.5 font-geist text-xs text-white transition-opacity hover:opacity-80"
                                    onClick={() => copyToClipboard(jsonExample, 'Body JSON')}
                                    type="button"
                                >
                                    <MdContentCopy size={14} />
                                    Copier
                                </button>
                            </div>
                        </div>

                        {/* curl example */}
                        <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                            <h3 className="font-geist text-base font-bold text-leadrs-text">Exemple cURL</h3>
                            <div className="relative mt-3">
                                <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3 pr-24 font-mono text-xs text-leadrs-text">{curlExample}</pre>
                                <button
                                    className="absolute right-2 top-2 flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-2.5 py-1.5 font-geist text-xs text-white transition-opacity hover:opacity-80"
                                    onClick={() => copyToClipboard(curlExample, 'Commande cURL')}
                                    type="button"
                                >
                                    <MdContentCopy size={14} />
                                    Copier
                                </button>
                            </div>
                        </div>

                        {/* Query params + response codes */}
                        <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                            <h3 className="font-geist text-base font-bold text-leadrs-text">Paramètre optionnel</h3>
                            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                Ajoutez <span className="font-mono">?landingPage=mon-site.fr</span> à l'URL pour identifier l'origine du lead dans vos analytics (même logique que pour le tracking de l'iframe).
                            </p>

                            <h3 className="mt-5 font-geist text-base font-bold text-leadrs-text">Réponses</h3>
                            <div className="mt-2 overflow-x-auto rounded-lg border border-leadrs-stroke">
                                <table className="w-full font-geist text-sm">
                                    <thead className="bg-leadrs-bg">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">Code</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">Signification</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-leadrs-stroke">
                                        {[
                                            ['200', 'Lead reçu (envoyé à un acheteur, mis en attente pour approbation, ou reçu sans acheteur disponible pour le moment).'],
                                            ['400', 'Body vide - aucun champ envoyé.'],
                                            ['403', 'Votre compte vendeur est actuellement bloqué.'],
                                            ['404', "URL invalide - l'identifiant du template est incorrect ou le template a été supprimé."],
                                            ['500', 'Erreur interne - réessayez ou contactez le support.'],
                                        ].map(([code, desc]) => (
                                            <tr key={code}>
                                                <td className="px-3 py-2 font-mono font-semibold text-leadrs-text">{code}</td>
                                                <td className="px-3 py-2 text-leadrs-text-soft">{desc}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Tab: Performances ── */}
            {tab === 'performances' && (
                <div className="flex flex-col gap-4">
                    {/* KPI strip */}
                    <div className={`grid grid-cols-2 gap-4 ${isSheetImport ? 'sm:grid-cols-3' : 'sm:grid-cols-4'}`}>
                        {[
                            ...(isSheetImport ? [] : [{ label: 'Vues', value: analyticsTotalViews }]),
                            { label: 'Leads générés', value: analyticsTotalLeads },
                            { label: 'Leads vendus', value: analyticsTotalSold },
                            { label: 'Revenus', value: `${Math.round(analyticsTotalRevenue)} €` },
                        ].map((kpi) => (
                            <div key={kpi.label} className="rounded-xl border border-leadrs-stroke bg-white p-5">
                                <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">{kpi.label}</p>
                                <p className="mt-1 font-crimson text-3xl font-semibold text-leadrs-text">{kpi.value}</p>
                            </div>
                        ))}
                    </div>

                    {/* AI insight card */}
                    {!isSheetImport && !analyticsLoading && analyticsStats.length > 0 && (() => {
                        const best = analyticsStats[0];
                        const convRate = best.views > 0 ? (best.count / best.views * 100).toFixed(1) : null;
                        const periodLabel = weeklyLabels.length > 0
                            ? `sur les ${weeklyLabels.length} dernier${weeklyLabels.length > 1 ? 's' : ''} mois`
                            : 'au total';
                        const displayUrl = best._id.replace(/^https?:\/\//, '');
                        return (
                            <div className="rounded-xl border border-leadrs-brand/20 bg-gradient-to-br from-leadrs-purple-soft to-white p-5">
                                <div className="flex items-start gap-3">
                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-brand/10">
                                        <svg className="h-4 w-4 text-leadrs-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-brand">Insight IA</p>
                                        <p className="mt-1 font-crimson text-lg font-semibold leading-snug text-leadrs-text">
                                            Votre meilleur site est{' '}
                                            <span className="text-leadrs-brand">{displayUrl}</span>
                                            {' '}, il a généré{' '}
                                            <span className="text-leadrs-brand">{best.count} lead{best.count > 1 ? 's' : ''}</span>
                                            {' '}{periodLabel}
                                            {convRate !== null && (
                                                <> avec un taux de conversion de{' '}
                                                <span className="text-leadrs-green-focus">{convRate}%</span></>
                                            )}.
                                        </p>
                                        {best.sold > 0 && (
                                            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                                {best.sold} lead{best.sold > 1 ? 's' : ''} vendu{best.sold > 1 ? 's' : ''} pour {Math.round(best.revenue)} € de revenus générés.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Monthly line chart — only shown when ≥ 3 months of data */}
                    {!isSheetImport && !weeklyLoading && weeklyLabels.length >= 3 && (
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        <h2 className="mb-1 font-crimson text-2xl font-semibold text-leadrs-text">Leads par mois</h2>
                        <p className="mb-4 font-geist text-xs text-leadrs-text-soft">Évolution mensuelle des leads générés par site d'intégration.</p>

                        {weeklyLoading && (
                            <div className="flex items-center justify-center py-16">
                                <div className="h-6 w-6 animate-spin rounded-full border-2 border-leadrs-stroke border-t-leadrs-brand" />
                            </div>
                        )}

                        {!weeklyLoading && weeklySeries.length === 0 && (
                            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-leadrs-stroke py-12">
                                <p className="font-crimson text-xl font-semibold text-leadrs-text">Aucune donnée</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">Les leads apparaîtront ici une fois générés.</p>
                            </div>
                        )}

                        {!weeklyLoading && weeklySeries.length > 0 && (
                            <ReactApexChart
                                type="line"
                                height={280}
                                series={weeklySeries}
                                options={{
                                    chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
                                    stroke: { curve: 'smooth', width: 2 },
                                    markers: { size: 4, hover: { size: 6 } },
                                    xaxis: {
                                        categories: weeklyLabels,
                                        labels: { style: { fontSize: '11px', colors: '#9CA3AF' } },
                                        axisBorder: { show: false },
                                        axisTicks: { show: false },
                                    },
                                    yaxis: {
                                        labels: { style: { fontSize: '11px', colors: '#9CA3AF' } },
                                        min: 0,
                                        forceNiceScale: true,
                                    },
                                    grid: { borderColor: '#F3F4F6', strokeDashArray: 4 },
                                    legend: { position: 'bottom', fontSize: '12px', fontFamily: 'inherit', markers: { width: 8, height: 8, radius: 4 } },
                                    tooltip: { shared: true, intersect: false, y: { formatter: (v: number) => `${v} lead${v > 1 ? 's' : ''}` } },
                                    colors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'],
                                    dataLabels: { enabled: false },
                                }}
                            />
                        )}
                    </div>
                    )}

                    {/* Per-site */}
                    {!isSheetImport && (
                    <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                        <h2 className="mb-4 font-crimson text-2xl font-semibold text-leadrs-text">Par site</h2>

                        {analyticsLoading && (
                            <div className="flex items-center justify-center py-16">
                                <div className="h-6 w-6 animate-spin rounded-full border-2 border-leadrs-stroke border-t-leadrs-brand" />
                            </div>
                        )}

                        {!analyticsLoading && analyticsStats.length === 0 && noLeadSites.length === 0 && (
                            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-leadrs-stroke py-16">
                                <svg className="mb-3 h-8 w-8 text-leadrs-text-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <p className="font-crimson text-xl font-semibold text-leadrs-text">Aucune donnée</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                    Les leads arrivant via ce formulaire apparaîtront ici, groupés par site.
                                </p>
                            </div>
                        )}

                        {!analyticsLoading && analyticsStats.length > 0 && (
                            <div className="flex flex-col gap-3">
                                {analyticsStats.map((s) => {
                                    const convRate = s.views > 0 ? (s.count / s.views * 100).toFixed(1) : null;
                                    return (
                                        <div key={s._id} className="rounded-xl border border-leadrs-stroke p-4">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <svg className="h-3.5 w-3.5 shrink-0 text-leadrs-text-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                                                    </svg>
                                                    <span className="truncate font-geist text-sm font-medium text-leadrs-text">{s._id.replace(/^https?:\/\//, '')}</span>
                                                </div>
                                                <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-1 font-geist text-sm">
                                                    {s.views > 0 && (
                                                        <span className="text-leadrs-text-soft">{s.views} vue{s.views > 1 ? 's' : ''}</span>
                                                    )}
                                                    <span className="text-leadrs-text-soft">{s.count} lead{s.count > 1 ? 's' : ''}</span>
                                                    <span className="font-semibold text-leadrs-green-focus">{s.sold} vendus</span>
                                                    {convRate !== null && (
                                                        <span className="w-14 text-right text-leadrs-text-soft">{convRate}% conv.</span>
                                                    )}
                                                    <span className="w-16 text-right font-semibold text-leadrs-brand">{Math.round(s.revenue)} €</span>
                                                </div>
                                            </div>
                                            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-leadrs-bg">
                                                <div className="h-full rounded-full bg-leadrs-brand" style={{ width: `${Math.round((s.count / analyticsMaxCount) * 100)}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {!analyticsLoading && noLeadSites.length > 0 && (
                            <div className="mt-4">
                                <p className="mb-2 font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">Sites intégrés sans lead</p>
                                <div className="flex flex-wrap gap-2">
                                    {noLeadSites.map(({ url, views }) => (
                                        <span key={url} className="inline-flex items-center gap-1.5 rounded-full border border-leadrs-stroke bg-leadrs-bg px-3 py-1 font-geist text-xs text-leadrs-text-soft">
                                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                                            </svg>
                                            {url.replace(/^https?:\/\//, '')}
                                            {views > 0 && <span className="ml-1 text-leadrs-text-soft/60">{views} vue{views > 1 ? 's' : ''}</span>}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                </div>
            )}

        </div>
    );
};

export default EditFormTemplate;
